import { pickNonUndefined } from '@domain/auth/auth.utils';
import { createReducer, INITIAL_STATE } from '../../../shared/redux';
import { ERROR_MESSAGES } from './sign-in.constants';


export const SIGN_IN = '@auth/SIGN_IN';
export const SIGN_IN_ACCEPTED = '@auth/SIGN_IN_ACCEPTED';
export const SIGN_IN_REJECTED = '@auth/SIGN_IN_REJECTED';
export const SIGN_IN_CHALLENGE_ISSUED = '@auth/SIGN_IN_CHALLENGE_ISSUED';
export const SIGN_IN_VALIDATION_EXCEPTION_RAISED = '@auth/SIGN_IN_VALIDATION_EXCEPTION_RAISED';
export const SIGN_IN_NETWORK_EXCEPTION_RAISED = '@auth/SIGN_IN_NETWORK_EXCEPTION_RAISED';
export const SIGN_IN_USER_NOT_CONFIRMED_EXCEPTION_RAISED = '@auth/SIGN_IN_USER_NOT_CONFIRMED_EXCEPTION_RAISED';
export const SIGN_IN_PASSWORD_RESET_REQUIRED_EXCEPTION_RAISED = '@auth/SIGN_IN_PASSWORD_RESET_REQUIRED_EXCEPTION_RAISED';
export const SIGN_IN_NOT_AUTHORIZED_EXCEPTION_RAISED = '@auth/SIGN_IN_NOT_AUTHORIZED_EXCEPTION_RAISED';
export const SIGN_IN_USER_NOT_FOUND_EXCEPTION_RAISED = '@auth/SIGN_IN_USER_NOT_FOUND_EXCEPTION_RAISED';
export const SIGN_IN_INVALID_PASSWORD_EXCEPTION_RAISED = '@auth/SIGN_IN_INVALID_PASSWORD_EXCEPTION_RAISED';
export const SIGN_IN_YUP_EXCEPTION_RAISED = '@auth/SIGN_IN_YUP_EXCEPTION_RAISED';
export const SIGN_IN_UNKNOWN_EXCEPTION_RAISED = '@auth/SIGN_IN_UNKNOWN_EXCEPTION_RAISED';
export const SET_SIGN_IN_FLAGS = '@auth/SET_SIGN_IN_FLAGS';
export const RESET_SIGN_IN_FLAGS = '@auth/RESET_SIGN_IN_FLAGS';
export const RESET_SIGN_IN_LOADING = '@auth/RESET_SIGN_IN_LOADING';
export const SIGN_IN_RESET = '@auth/SIGN_IN_RESET';
export const SIGN_IN_USER_SET = '@auth/SIGN_IN_USER_SET';
export const SIGN_IN_INVALID_PARAMETER_EXCEPTION_RAISED = '@auth/SIGN_IN_INVALID_PARAMETER_EXCEPTION_RAISED';
export const SIGN_IN_FEDERATED = '@auth/SIGN_IN_FEDERATED';

export const getSignInData = state => state.auth.signIn.data;
export const getSignInError = state => state.auth.signIn.error;
export const getSignInErrorMessage = state => state.auth.signIn.error?.message || null;
export const getUser = state => state.auth.signIn.user;
export const getUserName = state => state.auth.signIn.user.signInUserSession.idToken.payload.name;
export const isLoadingSignIn = state => state.auth.signIn.loading;
export const isSignInFromSignUp = state => state.auth.signIn.flags.fromSignUp;
export const isFromInvite = state => state.auth.signIn.flags.fromInvite;
export const isNewUser = state => state.auth.signIn.flags.newUser;
export const isPlatformIntegration = state => state.auth.signIn.flags.platformIntegration;
export const getUserPhoneNumber = state => (
    state.auth.signIn.user.signInUserSession.idToken.payload.phone_number
);
export const getSignInFlags = state => state.auth.signIn.flags;

export const signIn = ({
    username, password,
}) => ({
    type: SIGN_IN,
    payload: {
        username, password,
    },
});

export const signInFederated = () => ({
    type: SIGN_IN_FEDERATED,
    payload: {},
});

export const acceptSignIn = ({ landingRoute }) => ({
    type: SIGN_IN_ACCEPTED,
    payload: { landingRoute },
});

export const rejectSignIn = ({ code, error, fromSignUp = false }) => ({
    type: SIGN_IN_REJECTED,
    payload: { code, error, fromSignUp },
});

export const issueChallengeSignIn = ({ user }) => ({
    type: SIGN_IN_CHALLENGE_ISSUED,
    payload: { user },
});

export const resetSignIn = () => ({
    type: SIGN_IN_RESET,
    payload: {},
});

export const setUser = ({ user }) => ({
    type: SIGN_IN_USER_SET,
    payload: { user },
});

export const raiseValidationException = ({ error }) => ({
    type: SIGN_IN_VALIDATION_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseNetworkException = ({ error }) => ({
    type: SIGN_IN_NETWORK_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInUserNotConfirmed = ({ error }) => ({
    type: SIGN_IN_USER_NOT_CONFIRMED_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInPasswordResetRequired = ({ error }) => ({
    type: SIGN_IN_PASSWORD_RESET_REQUIRED_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInNotAuthorized = ({ error }) => ({
    type: SIGN_IN_NOT_AUTHORIZED_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInUserNotFound = ({ error }) => ({
    type: SIGN_IN_USER_NOT_FOUND_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInInvalidPasswordException = ({ error }) => ({
    type: SIGN_IN_INVALID_PASSWORD_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInYupException = ({ error }) => ({
    type: SIGN_IN_YUP_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseSignInUnknownException = ({ error }) => ({
    type: SIGN_IN_UNKNOWN_EXCEPTION_RAISED,
    payload: { error },
});

export const raiseInvalidParameterException = ({ error }) => ({
    type: SIGN_IN_INVALID_PARAMETER_EXCEPTION_RAISED,
    payload: { error },
});

export const resetSignInFlags = () => ({
    type: RESET_SIGN_IN_FLAGS,
    payload: {},
});

export const setSignInFlags = ({
    fromInvite, fromSignUp, newUser, platformIntegration, passwordStep, loadingFederatedLogin,
}) => ({
    type: SET_SIGN_IN_FLAGS,
    payload: pickNonUndefined({
        fromInvite,
        fromSignUp,
        newUser,
        platformIntegration,
        passwordStep,
        loadingFederatedLogin,
    }),
});

export const resetSignInLoading = () => ({
    type: RESET_SIGN_IN_LOADING,
    payload: {},
});

export const formatExceptionMessage = (error) => {
    const { message } = error;
    const formattedMessage = ERROR_MESSAGES[message]?.formatted;

    return { ...error, message: formattedMessage || error.message };
};

const updateError = (state = {}, action) => ({
    ...state,
    error: action.payload.error,
    loading: false,
});

const initialState = {
    error: null,
    loading: false,
    user: null,
    data: null,
    flags: {
        fromInvite: false,
        fromSignUp: false,
        newUser: false,
        platformIntegration: false,
        passwordStep: false,
    },
};

export const signInReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [SIGN_IN]: (state = {}, action) => ({
        ...state,
        error: null,
        loading: true,
        data: action.payload,
    }),
    [SIGN_IN_ACCEPTED]: (state = {}) => ({
        ...state,
        error: null,
        loading: false,
        data: null,
    }),
    [SIGN_IN_USER_SET]: (state = {}, action) => ({
        ...state,
        user: { ...state.user, ...action.payload.user },
        data: null,
    }),
    [SIGN_IN_CHALLENGE_ISSUED]: (state = {}, action) => ({
        ...state,
        error: null,
        loading: false,
        user: action.payload.user,
    }),
    [SET_SIGN_IN_FLAGS]: (state = {}, action) => ({
        ...state,
        flags: { ...state.flags, ...action.payload },
    }),
    [RESET_SIGN_IN_FLAGS]: (state = {}) => ({
        ...state,
        flags: {
            fromInvite: false,
            fromSignUp: false,
            newUser: false,
            platformIntegration: false,
            passwordStep: false,
        },
    }),
    [RESET_SIGN_IN_LOADING]: (state = {}) => ({
        ...state,
        loading: false,
    }),
    [SIGN_IN_RESET]: () => initialState,
    [SIGN_IN_VALIDATION_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_NETWORK_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_USER_NOT_CONFIRMED_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_PASSWORD_RESET_REQUIRED_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_NOT_AUTHORIZED_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_USER_NOT_FOUND_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_INVALID_PASSWORD_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_YUP_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_UNKNOWN_EXCEPTION_RAISED]: updateError,
    [SIGN_IN_INVALID_PARAMETER_EXCEPTION_RAISED]: updateError,
});
